import { create } from 'zustand'
import DOMPurify from 'dompurify'
import { dbFetch, dbFetchJson } from '../lib/dbFetch'
import useAuthStore from './authStore'

const sanitize = (s) => typeof s === 'string' ? DOMPurify.sanitize(s, { ALLOWED_TAGS: [] }) : s

// Mappers snake → camel (la query .select=* ritorna snake_case)
const mapList = (l) => l && ({
  id:             l.id,
  taskId:         l.task_id,
  userId:         l.user_id,
  title:          l.title,
  generatedByAi:  l.generated_by_ai,
  createdAt:      l.created_at,
  updatedAt:      l.updated_at,
})

const mapItem = (i) => i && ({
  id:           i.id,
  smartListId:  i.smart_list_id,
  text:         i.text,
  completed:    i.completed,
  position:     i.position,
  dueAt:        i.due_at,
  createdAt:    i.created_at,
})

/**
 * Smart Lists store: cache locale per task_id → { list, items[] }.
 * Caricamento on-demand via `loadForTask(taskId)`.
 * Tutte le mutation usano optimistic update + rollback on error.
 */
const useSmartListStore = create((set, get) => ({
  // { [taskId]: { list, items, loading, error } }
  cache: {},

  /** Carica la smart list di un task (se esiste). Cacha il risultato. */
  loadForTask: async (taskId) => {
    if (!taskId) return null
    set(s => ({ cache: { ...s.cache, [taskId]: { ...s.cache[taskId], loading: true, error: null } } }))
    try {
      const lists = await dbFetchJson(
        `smart_lists?task_id=eq.${taskId}&select=*`,
        'GET'
      )
      const list = lists?.[0]
      let items = []
      if (list) {
        items = await dbFetchJson(
          `smart_list_items?smart_list_id=eq.${list.id}&select=*&order=position.asc`,
          'GET'
        )
      }
      set(s => ({
        cache: {
          ...s.cache,
          [taskId]: {
            list: mapList(list),
            items: (items || []).map(mapItem),
            loading: false,
            error: null,
          },
        },
      }))
      return get().cache[taskId]
    } catch (err) {
      set(s => ({ cache: { ...s.cache, [taskId]: { ...s.cache[taskId], loading: false, error: err.message } } }))
      throw err
    }
  },

  /** Clona uno snapshot smart list ricevuto via share su un task appena
   *  importato dal recipient. Crea la list + bulk-insert degli items.
   *  Snapshot atteso: { title, items: [{ text, completed, position, dueAt }] }.
   *  Best-effort: errori vengono loggati ma non sollevano (il task è già
   *  stato creato; meglio una smart list mancante che un task perso). */
  cloneFromSnapshot: async (taskId, snapshot) => {
    if (!taskId || !snapshot) return null
    const userId = useAuthStore.getState().user?.id
    if (!userId) return null
    try {
      const created = await dbFetchJson('smart_lists', 'POST', {
        task_id: taskId,
        user_id: userId,
        title:   sanitize(snapshot.title) || null,
      })
      const newList = mapList(created?.[0])
      if (!newList) return null

      const itemRows = (snapshot.items ?? [])
        .filter(it => it && typeof it.text === 'string' && it.text.trim())
        .map((it, idx) => ({
          smart_list_id: newList.id,
          text:          sanitize(it.text),
          completed:     !!it.completed,
          position:      typeof it.position === 'number' ? it.position : idx,
          due_at:        it.dueAt || null,
        }))

      let mappedItems = []
      if (itemRows.length) {
        const itemsRes = await dbFetchJson('smart_list_items', 'POST', itemRows)
        mappedItems = (itemsRes || []).map(mapItem)
      }

      set(s => ({
        cache: {
          ...s.cache,
          [taskId]: { list: newList, items: mappedItems, loading: false, error: null },
        },
      }))
      return { list: newList, items: mappedItems }
    } catch (err) {
      console.warn('[smartListStore.cloneFromSnapshot]', err.message)
      return null
    }
  },

  /** Crea una smart list vuota per un task. */
  createList: async (taskId, title = '') => {
    const userId = useAuthStore.getState().user?.id
    if (!userId) throw new Error('Not authenticated')
    const created = await dbFetchJson('smart_lists', 'POST', {
      task_id: taskId,
      user_id: userId,
      title:   sanitize(title) || null,
    })
    const newList = mapList(created?.[0])
    set(s => ({
      cache: { ...s.cache, [taskId]: { list: newList, items: [], loading: false, error: null } },
    }))
    return newList
  },

  /** Aggiorna il titolo della lista. */
  updateListTitle: async (taskId, title) => {
    const cached = get().cache[taskId]
    if (!cached?.list) return
    const prevTitle = cached.list.title
    set(s => ({
      cache: { ...s.cache, [taskId]: { ...cached, list: { ...cached.list, title } } },
    }))
    try {
      await dbFetch(`smart_lists?id=eq.${cached.list.id}`, 'PATCH', { title: sanitize(title) || null })
    } catch (err) {
      set(s => ({ cache: { ...s.cache, [taskId]: { ...cached, list: { ...cached.list, title: prevTitle } } } }))
      throw err
    }
  },

  /** Elimina la lista (e i suoi item via CASCADE). */
  deleteList: async (taskId) => {
    const cached = get().cache[taskId]
    if (!cached?.list) return
    const prev = cached
    set(s => ({ cache: { ...s.cache, [taskId]: { list: null, items: [], loading: false, error: null } } }))
    try {
      await dbFetch(`smart_lists?id=eq.${cached.list.id}`, 'DELETE')
    } catch (err) {
      set(s => ({ cache: { ...s.cache, [taskId]: prev } }))
      throw err
    }
  },

  /** Aggiunge un item in fondo. Optimistic. */
  addItem: async (taskId, { text, dueAt = null }) => {
    const cached = get().cache[taskId]
    if (!cached?.list) throw new Error('List not loaded')
    const cleanText = sanitize(text)?.trim()
    if (!cleanText) return
    const nextPos = (cached.items[cached.items.length - 1]?.position ?? -1) + 1
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    const optimistic = {
      id: tempId,
      smartListId: cached.list.id,
      text: cleanText,
      completed: false,
      position: nextPos,
      dueAt,
      createdAt: new Date().toISOString(),
    }
    set(s => ({
      cache: { ...s.cache, [taskId]: { ...cached, items: [...cached.items, optimistic] } },
    }))
    try {
      const created = await dbFetchJson('smart_list_items', 'POST', {
        smart_list_id: cached.list.id,
        text: cleanText,
        position: nextPos,
        due_at: dueAt,
      })
      const real = mapItem(created?.[0])
      set(s => {
        const c = s.cache[taskId]
        if (!c) return s
        return {
          cache: {
            ...s.cache,
            [taskId]: { ...c, items: c.items.map(i => i.id === tempId ? real : i) },
          },
        }
      })
      return real
    } catch (err) {
      set(s => {
        const c = s.cache[taskId]
        if (!c) return s
        return { cache: { ...s.cache, [taskId]: { ...c, items: c.items.filter(i => i.id !== tempId) } } }
      })
      throw err
    }
  },

  /** Toggle completed di un item. Optimistic. */
  toggleItem: async (taskId, itemId) => {
    const cached = get().cache[taskId]
    if (!cached) return
    const item = cached.items.find(i => i.id === itemId)
    if (!item) return
    const newCompleted = !item.completed
    set(s => ({
      cache: { ...s.cache, [taskId]: { ...cached, items: cached.items.map(i => i.id === itemId ? { ...i, completed: newCompleted } : i) } },
    }))
    try {
      await dbFetch(`smart_list_items?id=eq.${itemId}`, 'PATCH', { completed: newCompleted })
    } catch (err) {
      set(s => ({ cache: { ...s.cache, [taskId]: { ...cached, items: cached.items.map(i => i.id === itemId ? { ...i, completed: item.completed } : i) } } }))
      throw err
    }
  },

  /** Aggiorna testo o due_at di un item. */
  updateItem: async (taskId, itemId, patch) => {
    const cached = get().cache[taskId]
    if (!cached) return
    const item = cached.items.find(i => i.id === itemId)
    if (!item) return
    const updates = { ...patch }
    if ('text' in updates) updates.text = sanitize(updates.text)
    set(s => ({
      cache: { ...s.cache, [taskId]: { ...cached, items: cached.items.map(i => i.id === itemId ? { ...i, ...patch } : i) } },
    }))
    try {
      const dbPatch = {}
      if ('text' in updates)  dbPatch.text   = updates.text
      if ('dueAt' in updates) dbPatch.due_at = updates.dueAt
      await dbFetch(`smart_list_items?id=eq.${itemId}`, 'PATCH', dbPatch)
    } catch (err) {
      set(s => ({ cache: { ...s.cache, [taskId]: { ...cached, items: cached.items.map(i => i.id === itemId ? item : i) } } }))
      throw err
    }
  },

  /** Riordina gli item di una smart list secondo `orderedIds` (lista ordinata
   *  degli id come dovranno apparire). Le nuove `position` vengono assegnate
   *  sequenzialmente 0..N-1. Optimistic: aggiorna subito la cache, poi PATCH
   *  in parallelo. Rollback su errore. */
  reorderItems: async (taskId, orderedIds) => {
    const cached = get().cache[taskId]
    if (!cached) return
    const prevItems = cached.items
    // Mappa id → nuovo index
    const posByid = new Map(orderedIds.map((id, idx) => [id, idx]))
    const nextItems = [...cached.items]
      .map(it => posByid.has(it.id) ? { ...it, position: posByid.get(it.id) } : it)
      .sort((a, b) => a.position - b.position)
    set(s => ({ cache: { ...s.cache, [taskId]: { ...cached, items: nextItems } } }))
    try {
      await Promise.all(
        orderedIds.map((id, idx) =>
          dbFetch(`smart_list_items?id=eq.${id}`, 'PATCH', { position: idx })
        )
      )
    } catch (err) {
      set(s => ({ cache: { ...s.cache, [taskId]: { ...cached, items: prevItems } } }))
      throw err
    }
  },

  /** Elimina un item. */
  deleteItem: async (taskId, itemId) => {
    const cached = get().cache[taskId]
    if (!cached) return
    const item = cached.items.find(i => i.id === itemId)
    if (!item) return
    set(s => ({
      cache: { ...s.cache, [taskId]: { ...cached, items: cached.items.filter(i => i.id !== itemId) } },
    }))
    try {
      await dbFetch(`smart_list_items?id=eq.${itemId}`, 'DELETE')
    } catch (err) {
      set(s => ({ cache: { ...s.cache, [taskId]: { ...cached, items: [...cached.items] } } }))  // ripristina (rimette semplice — order via position)
      throw err
    }
  },
}))

export default useSmartListStore
